import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import styled from "@emotion/styled";
import { css, Global } from "@emotion/react";
import Icon, { ICON_POSITION, ICON_SIZE, ICON_TYPE, withIcon } from "components/Icon";
import { colors } from "style";
import { formatNumber } from "utils";
import { Select, TextInput } from "components/Form";
import DatePicker from "components/Form/DatePicker";
import { DataCell } from "components/TermSheets";
import { DATE_FORMAT_TYPE } from "constants";
import { BUTTON_SIZE } from "components/Buttons";

/**
 * TermSheetInput
 *
 * @param {String}    type
 * @param {String}    value
 * @param {Function}  onChange
 * @param {Boolean}   onRevert
 * @param {String}    name
 * @param {Boolean}   hasChanges
 * @param {Boolean}   disabled
 * @param {Boolean}   readOnly
 */
const TermSheetInput = ({ type, value, onChange, onRevert, name, hasChanges, disabled, readOnly, ...props }) => {
  const [val, setVal] = useState(value);
  const [formattedVal, setFormattedVal] = useState(value);
  const [focus, setFocus] = useState(false);
  const formItemProps = { type, name, hasChanges, disabled, focus, readOnly };

  const ClearComponent = () => {
    if (!disabled && hasChanges && !focus) {
      return (
        <ClearButton onClick={onRevert} size={BUTTON_SIZE.small}>
          <Icon type={ICON_TYPE.close} size={ICON_SIZE.normal} color="inherit" />
        </ClearButton>
      );
    } else {
      return null;
    }
  };

  useEffect(() => {
    setVal(value);
  }, [value]);

  useEffect(() => {
    setFormattedVal(formatNumber(val, "", 2));
  }, [val]);

  switch (type) {
    /**
     * @description Date picker
     */
    case TERM_SHEET_INPUT_TYPES.date:
      return (
        <DataCell>
          <DateP value={value} onChange={onChange} hasChanges={hasChanges} {...formItemProps} {...props} />
          <ClearComponent />
        </DataCell>
      );

    /**
     * @description Select box
     */
    case TERM_SHEET_INPUT_TYPES.select:
      return (
        <DataCell>
          <StyledSelect
            value={val}
            onChange={(n, v) => {
              if (!v) return;
              onChange(name, v);
            }}
            menuPosition="fixed"
            {...formItemProps}
            {...props}
          />
          <ClearComponent />
        </DataCell>
      );

    /**
     * @description Plain text
     */
    case TERM_SHEET_INPUT_TYPES.text:
      return <Text>{value}</Text>;

    /**
     * @description Input
     */
    default:
      return (
        <>
          <Global styles={styles.termsheet_icon_override} />
          <DataCell isFocus={focus}>
            <Input
              value={focus && !disabled && !readOnly ? val : formattedVal}
              onChange={(n, v) => setVal(v)}
              onFocus={() => setFocus(true)}
              onBlur={() => {
                setFocus(false);
                // Only call onChange if the field is not readOnly and value has actually changed
                if (!readOnly && val !== value) {
                  onChange(name, val);
                }
              }}
              {...formItemProps}
              {...props}
            />
            <ClearComponent />
          </DataCell>
        </>
      );
  }
};

/**
 * Input
 *
 * @param {String} type
 */
const Input = ({ type, ...props }) => {
  switch (type) {
    case TERM_SHEET_INPUT_TYPES.currency:
      return <CurrencyInput {...props} />;
    case TERM_SHEET_INPUT_TYPES.currencyWithSelector:
      return <CurrencyWithSelectorInput {...props} />;
    case TERM_SHEET_INPUT_TYPES.percent:
      return <PercentInput {...props} />;
    case TERM_SHEET_INPUT_TYPES.hours:
      return <HoursInput {...props} />;
    case TERM_SHEET_INPUT_TYPES.number:
      return <StyledInput {...props} />;
    default:
      return <TextInput {...props} />;
  }
};

/**
 * DateP
 *
 * @param {String} name
 * @param {String} value
 * @param {Function} onChange
 */
const DateP = ({ name, value, onChange, ...props }) => (
  <>
    <Global styles={styles.date_picker_icon} />
    <StyledDatePicker
      name={name}
      required={true}
      format={DATE_FORMAT_TYPE.ddMMyyyy}
      popperPlacement={"bottom-start"}
      transparent={true}
      onChange={onChange}
      value={value}
      popperProps={{ strategy: "fixed" }}
      {...props}
    />
  </>
);

/**
 * CurrencyInput
 *
 * @param {String}   currency
 * @param {Boolean}  currencyLoading
 */
const CurrencyInput = ({ currency, currencyLoading, ...props }) =>
  currencyLoading ? `...` : <CurrencyInputWithIcon type={currency} {...props} />;

/**
 * CurrencyWithSelectorInput
 * Renders a currency selector dropdown alongside a rate input field
 *
 * @param {String}   value - The rate value
 * @param {String}   currency - Currently selected currency
 * @param {Function} onCurrencyChange - Callback when currency changes
 * @param {Array}    currencyOptions - Array of currency options
 * @param {Boolean}  hasChanges - Whether the field has changes
 * @param {Boolean}  disabled - Whether the field is disabled
 */
const CurrencyWithSelectorInput = ({
  value,
  currency,
  onCurrencyChange,
  currencyOptions = [],
  hasChanges,
  disabled,
  ...props
}) => (
  <CombinedFieldWrapper>
    <CurrencySelectStyled
      value={currency}
      options={currencyOptions}
      onChange={(_, newCurrency) => onCurrencyChange?.(newCurrency)}
      disabled={disabled}
      hasChanges={hasChanges}
      menuPosition="fixed"
    />
    <StyledInput value={value} disabled={disabled} hasChanges={hasChanges} {...props} />
  </CombinedFieldWrapper>
);

const TERM_SHEET_INPUT_TYPES = {
  text: "text",
  select: "select",
  number: "number",
  currency: "currency",
  currencyWithSelector: "currencyWithSelector",
  percent: "percent",
  date: "date",
  hours: "hours",
};

const ClearButton = styled.button`
  border: 0;
  border-radius: 0;
  width: 1.25rem;
  height: 1.25rem;
  background: ${colors.green};
  color: ${colors.lightGreen};
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 0;
  right: 0;
  border-bottom-left-radius: 0.25rem;
  transition: transform 300ms ease;
  z-index: 3;

  &:hover {
    background: ${colors.darkGreen};
  }
`;

const StyledSelect = styled(Select)`
  > div:first-of-type {
    border-width: 0;
    border-radius: 0;
    background: #fff;
    padding-left: 0.8rem;

    ${({ border }) =>
      border &&
      `
      border: 1px solid ${colors.grayAnatomyLight4};
    `}

    ${({ hasChanges }) =>
      hasChanges &&
      `
      color: ${colors.darkGreen};
      background: ${colors.lightGreen};
      border: 1px ${colors.green} solid;
    `}

    ${({ disabled }) =>
      disabled &&
      `
      color: ${colors.purpleRainDark6};
      background: ${colors.grayAnatomyLight4};
    `}
  }
`;

const StyledInput = styled(TextInput)`
  border-color: transparent;
  text-align: right;
  border-radius: 0;
  background: ${({ highlightColor }) => highlightColor || "#fff"};
  transition: none;

  ${({ removed }) =>
    removed &&
    `
    border-color: ${colors.grayAnatomyLight4};
    font-weight: 400;
  `}

  ${({ hasChanges }) =>
    hasChanges &&
    `
    color: ${colors.darkGreen};
    background: ${colors.lightGreen};
    border: 1px ${colors.green} solid;
  `}  
  
  ${({ removed }) =>
    !removed &&
    `
    &:hover {
      color: ${colors.purpleRainBase};
      border-color: transparent;
      background: ${colors.grayAnatomyLight5};
    }

    &:focus {
      color: ${colors.purpleRainBase};
      border-color: ${colors.purpleRainBase} !important;
      transition: box-shadow 300ms ease;
      background: #fff;
      box-shadow: none;
      z-index: 10;
    }

    &:disabled {
      border: 0 !important;
      color: ${colors.purpleRainDark6};
      font-weight: 600;
    }
  `}
`;

const Text = styled.div`
  text-align: left;
  background: #fff;
  display: flex;
  align-items: center;
  padding: 0 1rem;
  font-weight: 500;
`;

const StyledDatePicker = styled(DatePicker)`
  border: 0;

  ${({ hasChanges }) =>
    hasChanges &&
    `
    color: ${colors.darkGreen} !important;
    background: ${colors.lightGreen} !important;
    border: 1px ${colors.green} solid !important;
  `}
`;

/**
 * Higher-order component that adds icon with dynamic color support
 * Uses memoization to prevent creating new components on every render
 */
const withIconAndColor = (Component, iconConfig) => {
  const componentCache = new Map();

  const WithIconAndColorComponent = ({ iconColor, ...props }) => {
    const color = iconColor || colors.grayAnatomyLight2;

    // Check if we've already created a component for this color
    if (!componentCache.has(color)) {
      componentCache.set(
        color,
        withIcon(Component, {
          ...iconConfig,
          color,
        })
      );
    }

    const EnhancedComponent = componentCache.get(color);

    return <EnhancedComponent {...props} />;
  };

  WithIconAndColorComponent.propTypes = {
    iconColor: PropTypes.string,
  };

  WithIconAndColorComponent.displayName = `WithIconAndColor(${Component.displayName || Component.name || "Component"})`;

  return WithIconAndColorComponent;
};

const CurrencyInputWithIcon = withIconAndColor(StyledInput, {
  size: ICON_SIZE.small,
  position: ICON_POSITION.overlay,
});

const PercentInput = withIconAndColor(StyledInput, {
  type: ICON_TYPE.percent,
  size: ICON_SIZE.small,
  position: ICON_POSITION.overlay,
});

const HoursInput = withIconAndColor(StyledInput, {
  type: ICON_TYPE.hours,
  size: ICON_SIZE.small,
  position: ICON_POSITION.overlay,
});

const CombinedFieldWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

const CurrencySelectStyled = styled(Select)`
  flex: 0 0 80px;
  min-width: 80px;

  > div:first-of-type {
    border-radius: 0;
    background: #fff;
    padding: 0 0.5rem;

    ${({ hasChanges }) =>
      hasChanges &&
      `
      color: ${colors.darkGreen};
      background: ${colors.lightGreen};
      border: 1px ${colors.green} solid;
    `}

    ${({ disabled }) =>
      disabled &&
      `
      color: ${colors.purpleRainDark6};
      background: ${colors.grayAnatomyLight4};
    `}
  }
`;

const styles = {
  date_picker_icon: css`
    #datepicker-icon {
      z-index: 3 !important;
    }
  `,
  termsheet_icon_override: css`
    #with-icon-overlay {
      z-index: 3 !important;
    }
  `,
};

TermSheetInput.propTypes = {
  type: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  name: PropTypes.string,
  onRevert: PropTypes.func,
  hasChanges: PropTypes.bool,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
};

DateP.propTypes = {
  name: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
};

CurrencyInput.propTypes = {
  currencyLoading: PropTypes.bool,
  currency: PropTypes.string,
};

CurrencyWithSelectorInput.propTypes = {
  value: PropTypes.string,
  currency: PropTypes.string,
  onCurrencyChange: PropTypes.func,
  currencyOptions: PropTypes.array,
  hasChanges: PropTypes.bool,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
};

Input.propTypes = {
  type: PropTypes.string,
};

export { TERM_SHEET_INPUT_TYPES, StyledInput };

export default TermSheetInput;
